import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Chip,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Avatar,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add, Delete, CloudUpload, PhotoCamera, Phone, LocationOn } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import ProviderStripeConnect from '../components/ProviderStripeConnect';
import AddressAutocomplete from '../components/AddressAutocomplete';
import SalesTaxRatesEditor from '../components/SalesTaxRatesEditor';
import { getStatesFromZipCodes } from '../utils/zipToState';
import { mergeSalesTaxRatesForStates, normalizeSalesTaxRates, ProviderSalesTaxRate } from '../utils/salesTax';

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newZipCode, setNewZipCode] = useState('');
  const [bulkZipCodes, setBulkZipCodes] = useState('');
  const [serviceAreas, setServiceAreas] = useState<string[]>([]);
  const [salesTaxRates, setSalesTaxRates] = useState<ProviderSalesTaxRate[]>([]);
  const [salesTaxCoverageAttested, setSalesTaxCoverageAttested] = useState(false);
  const [services, setServices] = useState({
    lawnMowingTrimming: false,
    weedControlFertilizing: false,
    snowRemoval: false,
  });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, zipCode: '', affectedBids: 0 });
  const [bulkEntryMode, setBulkEntryMode] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({
    insuranceProvider: '',
    insurancePolicyNumber: '',
    insuranceExpiryDate: '',
  });
  const [insuranceDocument, setInsuranceDocument] = useState<File | null>(null);
  const [insuranceUploading, setInsuranceUploading] = useState(false);
  const [pesticideLicenseForm, setPesticideLicenseForm] = useState({
    pesticideLicenseNumber: '',
    pesticideLicenseExpiryDate: '',
  });
  const [pesticideLicenseDocument, setPesticideLicenseDocument] = useState<string>('');
  const [pesticideLicenseUploading, setPesticideLicenseUploading] = useState(false);
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailValue, setEmailValue] = useState<string>('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [addressEditing, setAddressEditing] = useState(false);
  const [addressForm, setAddressForm] = useState({ street: '', city: '', state: 'IL', zipCode: '' });
  const [addressSaving, setAddressSaving] = useState(false);
  const [phoneEditing, setPhoneEditing] = useState(false);
  const [phoneValue, setPhoneValue] = useState<string>('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  useEffect(() => {
    if (user?.email) {
      setEmailValue(user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    const addr = (user as any)?.address;
    if (addr) {
      setAddressForm({
        street: addr.street || '',
        city: addr.city || '',
        state: addr.state || 'IL',
        zipCode: addr.zipCode || '',
      });
    }
  }, [(user as any)?.address]);

  useEffect(() => {
    setPhoneValue((user as any)?.phone || '');
  }, [(user as any)?.phone]);

  useEffect(() => {
    if (user?.userType?.toLowerCase() === 'provider') {
      const areas = (user as any).serviceAreas || [];
      setServiceAreas(areas);
      const states = getStatesFromZipCodes(areas);
      setSalesTaxRates(
        mergeSalesTaxRatesForStates(normalizeSalesTaxRates((user as any).salesTaxRates), states)
      );
      setSalesTaxCoverageAttested(!!(user as any).salesTaxCoverageAttested);
    }
    
    // Initialize services from user data
    if (user?.userType?.toLowerCase() === 'provider' && (user as any).services) {
      const userServices = (user as any).services || [];
      setServices({
        lawnMowingTrimming: userServices.includes('mowing'),
        weedControlFertilizing: userServices.includes('fertilizing'),
        snowRemoval: userServices.includes('snow_removal'),
      });
    }
    
    // Initialize insurance form with existing data
    if (user?.userType?.toLowerCase() === 'provider') {
      setInsuranceForm({
        insuranceProvider: (user as any).insuranceProvider || '',
        insurancePolicyNumber: (user as any).insurancePolicyNumber || '',
        insuranceExpiryDate: (user as any).insuranceExpiryDate ? new Date((user as any).insuranceExpiryDate).toISOString().split('T')[0] : '',
      });
      
      // Initialize pesticide license form with existing data
      setPesticideLicenseForm({
        pesticideLicenseNumber: (user as any).pesticideLicenseNumber || '',
        pesticideLicenseExpiryDate: (user as any).pesticideLicenseExpiryDate ? new Date((user as any).pesticideLicenseExpiryDate).toISOString().split('T')[0] : '',
      });
      setPesticideLicenseDocument((user as any).pesticideLicenseDocumentUrl || '');
    }
  }, [user]);

  const syncSalesTaxRatesForAreas = (areas: string[], existingRates?: ProviderSalesTaxRate[]) => {
    const prevStates = getStatesFromZipCodes(serviceAreas);
    const states = getStatesFromZipCodes(areas);
    setSalesTaxRates(
      mergeSalesTaxRatesForStates(existingRates ?? salesTaxRates, states)
    );
    // New state(s) from added ZIPs require re-attestation
    if (states.some((s) => !prevStates.includes(s))) {
      setSalesTaxCoverageAttested(false);
    }
  };

  const validateZipCode = (zipCode: string) => {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zipCode);
  };

  const handleAddZipCode = () => {
    const trimmedZip = newZipCode.trim();
    
    if (!trimmedZip) {
      setError('Please enter a zip code');
      return;
    }

    if (!validateZipCode(trimmedZip)) {
      setError('Please enter a valid zip code (e.g., 12345 or 12345-6789)');
      return;
    }

    if (serviceAreas.includes(trimmedZip)) {
      setError('This zip code is already in your service areas');
      return;
    }

    setServiceAreas([...serviceAreas, trimmedZip]);
    syncSalesTaxRatesForAreas([...serviceAreas, trimmedZip]);
    setNewZipCode('');
    setError('');
  };

  const handleAddBulkZipCodes = () => {
    if (!bulkZipCodes.trim()) {
      setError('Please enter zip codes');
      return;
    }

    // Split by commas, spaces, newlines, or any combination
    const zipList = bulkZipCodes
      .split(/[\s,\n]+/)
      .map(z => z.trim())
      .filter(z => z.length > 0);

    // Validate and filter
    const validZips: string[] = [];
    const invalidZips: string[] = [];
    const duplicates: string[] = [];

    zipList.forEach(zip => {
      if (!validateZipCode(zip)) {
        invalidZips.push(zip);
      } else if (serviceAreas.includes(zip)) {
        duplicates.push(zip);
      } else if (!validZips.includes(zip)) {
        validZips.push(zip);
      }
    });

    // Add valid zips
    if (validZips.length > 0) {
      const nextAreas = [...serviceAreas, ...validZips];
      setServiceAreas(nextAreas);
      syncSalesTaxRatesForAreas(nextAreas);
      setBulkZipCodes('');
      setBulkEntryMode(false);
      setSuccess(`Added ${validZips.length} zip code(s)!`);
      setError('');
    }

    // Show warnings
    if (invalidZips.length > 0) {
      setError(`${invalidZips.length} invalid zip code(s) skipped: ${invalidZips.slice(0, 5).join(', ')}${invalidZips.length > 5 ? '...' : ''}`);
    } else if (duplicates.length > 0) {
      setError(`${duplicates.length} duplicate(s) skipped (already in your list)`);
    }
  };

  const handleRemoveZipCode = async (zipCode: string) => {
    try {
      // Check how many bids would be affected
      const response = await apiService.get(`/bids/count-by-zipcode/${zipCode}`);
      const affectedBids = (response.data as any)?.count || 0;
      
      setConfirmDialog({ open: true, zipCode, affectedBids });
    } catch (err) {
      console.error('Failed to check affected bids:', err);
      // Proceed with removal even if we can't check
      setConfirmDialog({ open: true, zipCode, affectedBids: 0 });
    }
  };

  const confirmRemoveZipCode = async () => {
    const { zipCode } = confirmDialog;
    const updatedAreas = serviceAreas.filter(area => area !== zipCode);
    const updatedRates = mergeSalesTaxRatesForStates(salesTaxRates, getStatesFromZipCodes(updatedAreas));
    setServiceAreas(updatedAreas);
    setSalesTaxRates(updatedRates);
    setConfirmDialog({ open: false, zipCode: '', affectedBids: 0 });
    
    // Save changes immediately when removing (state list only shrinks — keep attestation)
    await saveChanges(updatedAreas, undefined, updatedRates, salesTaxCoverageAttested);
  };

  const saveChanges = async (
    areas: string[] = serviceAreas,
    servicesToSave?: typeof services,
    ratesToSave: ProviderSalesTaxRate[] = salesTaxRates,
    attested: boolean = salesTaxCoverageAttested
  ) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (ratesToSave.length > 0 && !attested) {
        setError('Please confirm that all states for which you are responsible for sales tax remittance are listed');
        setLoading(false);
        return;
      }

      const updateData: any = {
        serviceAreas: areas,
        salesTaxRates: ratesToSave,
        salesTaxCoverageAttested: attested,
      };

      // Include services if provided
      if (servicesToSave !== undefined) {
        updateData.services = Object.entries(servicesToSave)
          .filter(([_, selected]) => selected)
          .map(([service]) => {
            const serviceMap: { [key: string]: string } = {
              'lawnMowingTrimming': 'mowing',
              'weedControlFertilizing': 'fertilizing',
              'snowRemoval': 'snow_removal',
            };
            return serviceMap[service] || service;
          });
      }

      const response = await apiService.put('/auth/profile', updateData);

      if (response.success) {
        // Update the user context
        updateUser(response.data as any);
        setSuccess(servicesToSave !== undefined ? 'Services and service areas updated successfully!' : 'Service areas updated successfully!');
        
        // If we removed zip codes, trigger bid cleanup
        if (areas.length < serviceAreas.length) {
          await apiService.post('/bids/cleanup-by-service-areas', {
            serviceAreas: areas,
          });
        }
      } else {
        setError(response.error || response.message || 'Failed to update profile');
      }
    } catch (err: any) {
      // Handle backend validation errors (e.g., active commitments)
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError(err.message || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleServiceChange = (service: string, checked: boolean) => {
    setServices(prev => ({
      ...prev,
      [service]: checked,
    }));
  };

  const saveServices = async () => {
    // Check which services are being removed
    const currentServices = user?.userType === 'provider' ? ((user as any).services || []) : [];
    const newServices = Object.entries(services)
      .filter(([_, selected]) => selected)
      .map(([service]) => {
        const serviceMap: { [key: string]: string } = {
          'lawnMowingTrimming': 'mowing',
          'weedControlFertilizing': 'fertilizing',
          'snowRemoval': 'snow_removal',
        };
        return serviceMap[service] || service;
      });
    
    const removedServices = currentServices.filter((s: string) => !newServices.includes(s));
    
    if (removedServices.length > 0) {
      // The backend will validate and return an error if there are active commitments
      // We'll let the backend handle the validation and show the error message
    }
    
    await saveChanges(serviceAreas, services);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAddZipCode();
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be smaller than 5MB');
      return;
    }

    try {
      setLogoUploading(true);
      setError('');
      setSuccess('');

      // Convert image to base64 for now (in production, you'd upload to a cloud service)
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64String = e.target?.result as string;
        
        // Update profile with the new logo
        const response = await apiService.put('/auth/profile', {
          businessLogo: base64String,
        });

        if (response.success) {
          updateUser(response.data as any);
          setSuccess('Business logo updated successfully!');
        } else {
          setError('Failed to update business logo');
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleInsuranceFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInsuranceForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleInsuranceDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setInsuranceDocument(file);
    }
  };

  const handleInsuranceSubmit = async () => {
    setInsuranceUploading(true);
    setError('');
    setSuccess('');

    try {
      const updateData: any = {
        insuranceProvider: insuranceForm.insuranceProvider,
        insurancePolicyNumber: insuranceForm.insurancePolicyNumber,
        insuranceExpiryDate: insuranceForm.insuranceExpiryDate,
      };

      // If there's a new document, upload it first
      if (insuranceDocument) {
        const formData = new FormData();
        formData.append('insuranceDocument', insuranceDocument);
        
        const uploadResponse = await apiService.post('/auth/upload-insurance', formData);
        if (uploadResponse.success) {
          updateData.insuranceProof = (uploadResponse.data as any).url;
        }
      }

      const response = await apiService.put('/auth/profile', updateData);

      if (response.success) {
        updateUser(response.data as any);
        setSuccess('Insurance information updated successfully!');
        setInsuranceDocument(null);
        // Reset file input
        const fileInput = document.getElementById('insurance-document') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setError('Failed to update insurance information');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update insurance information');
    } finally {
      setInsuranceUploading(false);
    }
  };

  const getInsuranceStatus = () => {
    if (!user || user.userType !== 'provider') return null;
    
    const provider = user as any;
    const hasInsurance = provider.insuranceProvider && provider.insurancePolicyNumber && provider.insuranceExpiryDate;
    
    if (!hasInsurance) {
      return { status: 'missing', message: 'Insurance information required', color: 'error' };
    }
    
    const expiryDate = new Date(provider.insuranceExpiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', message: 'Insurance has expired', color: 'error' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', message: `Insurance expires in ${daysUntilExpiry} days`, color: 'warning' };
    } else {
      return { status: 'valid', message: 'Insurance is current', color: 'success' };
    }
  };

  const getPesticideLicenseStatus = () => {
    if (!user || (user as any).userType?.toLowerCase() !== 'provider') {
      return null;
    }
    const provider = user as any;
    const offersWeedControl = services.weedControlFertilizing;
    
    if (!offersWeedControl) {
      return null; // Don't show status if provider doesn't offer weed control
    }
    
    const hasLicense = provider.pesticideLicenseOnFile && provider.pesticideLicenseDocumentUrl && provider.pesticideLicenseNumber;
    
    if (!hasLicense) {
      return { status: 'missing', message: 'Pesticide license required for weed control services', color: 'error' };
    }
    
    if (provider.pesticideLicenseExpiryDate) {
      const expiryDate = new Date(provider.pesticideLicenseExpiryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) {
        return { status: 'expired', message: 'Pesticide license has expired', color: 'error' };
      } else if (daysUntilExpiry <= 30) {
        return { status: 'expiring', message: `Pesticide license expires in ${daysUntilExpiry} days`, color: 'warning' };
      } else {
        return { status: 'valid', message: 'Pesticide license is current', color: 'success' };
      }
    }
    
    return { status: 'valid', message: 'Pesticide license on file', color: 'success' };
  };

  const handlePesticideLicenseFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPesticideLicenseForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePesticideLicenseDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (allow images and PDFs)
    const validTypes = ['image/', 'application/pdf'];
    if (!validTypes.some(type => file.type.startsWith(type))) {
      setError('Please select a valid image or PDF file for pesticide license');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Pesticide license document must be smaller than 10MB');
      return;
    }

    try {
      setPesticideLicenseUploading(true);
      setError('');

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target?.result as string;
        setPesticideLicenseDocument(base64String);
        setPesticideLicenseUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError(err.message || 'Failed to upload pesticide license');
      setPesticideLicenseUploading(false);
    }
  };

  const handlePesticideLicenseSubmit = async () => {
    setPesticideLicenseUploading(true);
    setError('');
    setSuccess('');

    try {
      if (!pesticideLicenseDocument || !pesticideLicenseForm.pesticideLicenseNumber) {
        setError('License document and license number are required');
        setPesticideLicenseUploading(false);
        return;
      }

      const response = await apiService.put('/providers/pesticide-license', {
        pesticideLicenseDocument: pesticideLicenseDocument,
        pesticideLicenseNumber: pesticideLicenseForm.pesticideLicenseNumber,
        pesticideLicenseExpiryDate: pesticideLicenseForm.pesticideLicenseExpiryDate || undefined,
        pesticideLicenseOnFile: true,
        pesticideLicenseSubmittedDate: new Date().toISOString(),
      });

      if (response.success) {
        setSuccess('Pesticide license information updated successfully!');
        updateUser(response.data as any);
        setPesticideLicenseDocument('');
        const fileInput = document.getElementById('pesticide-license-document') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setError('Failed to update pesticide license information');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update pesticide license information');
    } finally {
      setPesticideLicenseUploading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Profile
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box sx={{ mt: 3 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Name:</strong> {user?.firstName} {user?.lastName}
          </Typography>

          {/* Customer: Address (editable) */}
          {user?.userType?.toLowerCase() === 'customer' && (
            <Box sx={{ mb: 2 }}>
              {!addressEditing ? (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body1">
                      <strong>Address:</strong>{' '}
                      {(user as any)?.address?.street || (user as any)?.address?.city || (user as any)?.address?.zipCode
                        ? [(user as any).address.street, (user as any).address.city, (user as any).address.state, (user as any).address.zipCode].filter(Boolean).join(', ')
                        : 'Not set'}
                    </Typography>
                  </Box>
                  <Button variant="outlined" size="small" onClick={() => setAddressEditing(true)} sx={{ mt: 0.5 }}>
                    Edit Address
                  </Button>
                </>
              ) : (
                <Box>
                  <AddressAutocomplete
                    value={addressForm.street}
                    onChange={(_, comp) => setAddressForm(prev => ({
                      ...prev,
                      street: comp.street || prev.street,
                      city: comp.city || prev.city,
                      state: comp.state || prev.state,
                      zipCode: comp.zipCode || prev.zipCode,
                    }))}
                    label="Street Address"
                    disabled={addressSaving}
                  />
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="City" value={addressForm.city} onChange={e => setAddressForm(prev => ({ ...prev, city: e.target.value }))} disabled={addressSaving} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth label="State" value={addressForm.state} onChange={e => setAddressForm(prev => ({ ...prev, state: e.target.value }))} disabled={addressSaving} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField fullWidth label="ZIP Code" value={addressForm.zipCode} onChange={e => setAddressForm(prev => ({ ...prev, zipCode: e.target.value }))} disabled={addressSaving} />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small" disabled={addressSaving || !addressForm.street?.trim() || !addressForm.city?.trim() || !addressForm.zipCode?.trim()} onClick={async () => {
                      try {
                        setAddressSaving(true); setError(''); setSuccess('');
                        const res = await apiService.put('/auth/profile', { address: addressForm });
                        if (res.success) { setSuccess('Address updated'); updateUser({ ...user, address: addressForm } as any); setAddressEditing(false); }
                        else setError(res.error || 'Failed to update address');
                      } catch (err: any) { setError(err.response?.data?.error || err.message || 'Failed to update address'); }
                      finally { setAddressSaving(false); }
                    }}>{addressSaving ? 'Saving...' : 'Save'}</Button>
                    <Button variant="outlined" size="small" onClick={() => { setAddressEditing(false); setAddressForm((user as any)?.address ? { street: (user as any).address.street || '', city: (user as any).address.city || '', state: (user as any).address.state || 'IL', zipCode: (user as any).address.zipCode || '' } : { street: '', city: '', state: 'IL', zipCode: '' }); }} disabled={addressSaving}>Cancel</Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Customer: Phone (editable) */}
          {user?.userType?.toLowerCase() === 'customer' && (
            <Box sx={{ mb: 2 }}>
              {!phoneEditing ? (
                <>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body1"><strong>Phone:</strong> {(user as any)?.phone || 'Not set'}</Typography>
                  </Box>
                  <Button variant="outlined" size="small" onClick={() => setPhoneEditing(true)} sx={{ mt: 0.5 }}>Edit Phone</Button>
                </>
              ) : (
                <Box>
                  <TextField label="Phone" value={phoneValue} onChange={e => setPhoneValue(e.target.value)} fullWidth disabled={phoneSaving} placeholder="(555) 123-4567" />
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button variant="contained" size="small" disabled={phoneSaving} onClick={async () => {
                      try {
                        setPhoneSaving(true); setError(''); setSuccess('');
                        const res = await apiService.put('/auth/profile', { phone: phoneValue.trim() || undefined });
                        if (res.success) { setSuccess('Phone updated'); updateUser({ ...user, phone: phoneValue.trim() || undefined } as any); setPhoneEditing(false); }
                        else setError(res.error || 'Failed to update phone');
                      } catch (err: any) { setError(err.response?.data?.error || err.message || 'Failed to update phone'); }
                      finally { setPhoneSaving(false); }
                    }}>{phoneSaving ? 'Saving...' : 'Save'}</Button>
                    <Button variant="outlined" size="small" onClick={() => { setPhoneEditing(false); setPhoneValue((user as any)?.phone || ''); }} disabled={phoneSaving}>Cancel</Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            {!emailEditing && user?.userType !== 'crew' ? (
              <>
                <Typography variant="body1" gutterBottom>
                  <strong>Email:</strong> {user?.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, mb: 1 }}>
                  This email address is used for your account login username.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setEmailEditing(true);
                    setEmailValue(user?.email || '');
                  }}
                  sx={{ mt: 1 }}
                >
                  Edit Email
                </Button>
              </>
            ) : user?.userType !== 'crew' ? (
              <Box>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  helperText={emailValue.trim() === '' 
                    ? 'Email is required. You cannot remove your email address without providing a replacement. This email is used for your account login username.'
                    : 'This email address is used for your account login username.'}
                  margin="normal"
                  disabled={emailSaving}
                  error={(emailValue !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) || emailValue.trim() === ''}
                />
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={async () => {
                      const newEmail = emailValue.trim();
                      if (!newEmail) {
                        setError('Email is required');
                        return;
                      }
                      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
                        setError('Invalid email format');
                        return;
                      }
                      if (newEmail === user?.email) {
                        setEmailEditing(false);
                        return;
                      }
                      try {
                        setEmailSaving(true);
                        setError('');
                        setSuccess('');
                        const response = await apiService.put('/auth/profile', {
                          email: newEmail
                        });
                        if (response.success) {
                          setSuccess('Email updated successfully!');
                          updateUser({ ...user, email: newEmail } as any);
                          setEmailEditing(false);
                        } else {
                          setError(response.error || 'Failed to update email');
                        }
                      } catch (err: any) {
                        setError(err.response?.data?.error || err.message || 'Failed to update email');
                      } finally {
                        setEmailSaving(false);
                      }
                    }}
                    disabled={emailSaving || emailValue.trim() === user?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)}
                  >
                    {emailSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setEmailEditing(false);
                      setEmailValue(user?.email || '');
                      setError('');
                    }}
                    disabled={emailSaving}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {user?.email}
              </Typography>
            )}
          </Box>
          
          {user?.userType?.toLowerCase() === 'provider' && (
            <>
              <Typography variant="body1" gutterBottom sx={{ mt: 3 }}>
                <strong>Business Name:</strong> {(user as any).businessName}
              </Typography>
              {(user as any).address && ((user as any).address.street || (user as any).address.city || (user as any).address.zipCode) && (
                <Typography variant="body1" gutterBottom>
                  <strong>Business Address:</strong>{' '}
                  {[(user as any).address.street, (user as any).address.city, (user as any).address.state, (user as any).address.zipCode].filter(Boolean).join(', ')}
                </Typography>
              )}
              
              {/* Business Logo Section */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Business Logo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload your business logo to make your profile more professional and recognizable to customers.
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    src={(user as any).businessLogo}
                    sx={{ width: 80, height: 80 }}
                  >
                    <PhotoCamera />
                  </Avatar>
                  
                  <Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="logo-upload"
                      type="file"
                      onChange={handleLogoUpload}
                      disabled={logoUploading}
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={logoUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                        disabled={logoUploading}
                      >
                        {logoUploading ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </label>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Recommended: Square image, max 5MB
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Service Areas (Zip Codes)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add or remove zip codes where you provide services. You can paste multiple zip codes at once.
                </Typography>
                
                {!bulkEntryMode ? (
                  <>
                    {/* Single zip code entry */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        size="small"
                        label="Add Zip Code"
                        value={newZipCode}
                        onChange={(e) => setNewZipCode(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="e.g., 12345"
                        sx={{ minWidth: 150 }}
                      />
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddZipCode}
                        disabled={loading}
                      >
                        Add
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setBulkEntryMode(true)}
                        disabled={loading}
                      >
                        Add Multiple
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Bulk zip code entry */}
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Paste Multiple Zip Codes"
                        value={bulkZipCodes}
                        onChange={(e) => setBulkZipCodes(e.target.value)}
                        placeholder="Paste zip codes here (comma, space, or line separated):&#10;60401, 60402, 60403&#10;or:&#10;60401&#10;60402&#10;60403"
                        helperText={`${bulkZipCodes ? bulkZipCodes.split(/[\s,\n]+/).filter(z => z.trim() && validateZipCode(z.trim())).length : 0} valid zip codes detected`}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          variant="contained"
                          onClick={handleAddBulkZipCodes}
                          disabled={loading || !bulkZipCodes.trim()}
                        >
                          Add All Zip Codes
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setBulkEntryMode(false);
                            setBulkZipCodes('');
                            setError('');
                          }}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}

                {/* Current service areas */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {serviceAreas.map((zipCode) => (
                    <Chip
                      key={zipCode}
                      label={zipCode}
                      onDelete={() => handleRemoveZipCode(zipCode)}
                      deleteIcon={<Delete />}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                  {serviceAreas.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No service areas added yet
                    </Typography>
                  )}
                </Box>

                <SalesTaxRatesEditor
                  rates={salesTaxRates}
                  onChange={(next) => {
                    setSalesTaxRates(next);
                  }}
                  disabled={loading}
                  coverageAttested={salesTaxCoverageAttested}
                  onCoverageAttestedChange={setSalesTaxCoverageAttested}
                />

                {/* Save button */}
                <Button
                  variant="contained"
                  onClick={() => saveChanges()}
                  disabled={loading || (salesTaxRates.length > 0 && !salesTaxCoverageAttested)}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save Service Areas & Sales Tax'}
                </Button>
              </Box>

              {/* Services Section */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Services You Offer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the services you can provide. You will only see service requests for the services you select.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={services.lawnMowingTrimming}
                        onChange={(e) => handleServiceChange('lawnMowingTrimming', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Lawn Mowing/Trimming"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={services.weedControlFertilizing}
                        onChange={(e) => handleServiceChange('weedControlFertilizing', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Weed Control/Fertilizing"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={services.snowRemoval}
                        onChange={(e) => handleServiceChange('snowRemoval', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Snow Removal"
                  />
                </Box>

                {/* Save button */}
                <Button
                  variant="contained"
                  onClick={saveServices}
                  disabled={loading || Object.values(services).every(v => !v)}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save Services'}
                </Button>
                {Object.values(services).every(v => !v) && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    Please select at least one service
                  </Typography>
                )}
              </Box>

              {/* Insurance Information Section */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  My Insurance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Complete your insurance information to submit bids on service requests. This information is required for liability protection.
                </Typography>

                {/* Insurance Status */}
                {(() => {
                  const status = getInsuranceStatus();
                  if (status) {
                    return (
                      <Alert severity={status.color as any} sx={{ mb: 3 }}>
                        <strong>Insurance Status:</strong> {status.message}
                      </Alert>
                    );
                  }
                  return null;
                })()}

                {/* Current Insurance Information */}
                {(user as any).insuranceProvider && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Current Insurance Information:
                    </Typography>
                    <Typography variant="body2">
                      <strong>Provider:</strong> {(user as any).insuranceProvider}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Policy Number:</strong> {(user as any).insurancePolicyNumber}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Expiry Date:</strong> {(user as any).insuranceExpiryDate ? new Date((user as any).insuranceExpiryDate).toLocaleDateString() : 'Not set'}
                    </Typography>
                    {(user as any).insuranceProof && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Document:</strong>{' '}
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            const docUrl = (user as any).insuranceProof;
                            if (docUrl.startsWith('data:')) {
                              // For base64 data URIs, create a blob and open in new window
                              const byteString = atob(docUrl.split(',')[1]);
                              const mimeString = docUrl.split(',')[0].split(':')[1].split(';')[0];
                              const ab = new ArrayBuffer(byteString.length);
                              const ia = new Uint8Array(ab);
                              for (let i = 0; i < byteString.length; i++) {
                                ia[i] = byteString.charCodeAt(i);
                              }
                              const blob = new Blob([ab], { type: mimeString });
                              const url = URL.createObjectURL(blob);
                              window.open(url, '_blank');
                              // Clean up the URL after a delay
                              setTimeout(() => URL.revokeObjectURL(url), 100);
                            } else {
                              window.open(docUrl, '_blank');
                            }
                          }}
                          sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                        >
                          View Insurance Document
                        </Button>
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Insurance Form */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Insurance Provider"
                      name="insuranceProvider"
                      value={insuranceForm.insuranceProvider}
                      onChange={handleInsuranceFormChange}
                      placeholder="e.g., State Farm, Allstate"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Policy Number"
                      name="insurancePolicyNumber"
                      value={insuranceForm.insurancePolicyNumber}
                      onChange={handleInsuranceFormChange}
                      placeholder="Enter policy number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Expiry Date"
                      name="insuranceExpiryDate"
                      type="date"
                      value={insuranceForm.insuranceExpiryDate}
                      onChange={handleInsuranceFormChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <input
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="insurance-document"
                        type="file"
                        onChange={handleInsuranceDocumentUpload}
                        disabled={insuranceUploading}
                      />
                      <label htmlFor="insurance-document">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={insuranceUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                          disabled={insuranceUploading}
                          fullWidth
                        >
                          {insuranceDocument ? insuranceDocument.name : 'Upload Insurance Document'}
                        </Button>
                      </label>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Upload insurance certificate (PDF, JPG, PNG)
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  onClick={handleInsuranceSubmit}
                  disabled={insuranceUploading || !insuranceForm.insuranceProvider || !insuranceForm.insurancePolicyNumber || !insuranceForm.insuranceExpiryDate}
                  sx={{ mt: 2 }}
                >
                  {insuranceUploading ? <CircularProgress size={20} /> : 'Update Insurance Information'}
                </Button>
              </Box>

              {/* Pesticide License Section - Only show if provider offers weed control */}
              {services.weedControlFertilizing && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Pesticide License
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Providers offering Weed Control/Fertilizing services must upload a valid IDOA-issued Commercial Pesticide Applicator License.
                  </Typography>

                  {/* Pesticide License Status */}
                  {(() => {
                    const status = getPesticideLicenseStatus();
                    if (status) {
                      return (
                        <Alert severity={status.color as any} sx={{ mb: 3 }}>
                          <strong>License Status:</strong> {status.message}
                        </Alert>
                      );
                    }
                    return null;
                  })()}

                  {/* Current Pesticide License Information */}
                  {(user as any).pesticideLicenseOnFile && (user as any).pesticideLicenseNumber && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Current Pesticide License Information:
                      </Typography>
                      <Typography variant="body2">
                        <strong>License Number:</strong> {(user as any).pesticideLicenseNumber}
                      </Typography>
                      {(user as any).pesticideLicenseExpiryDate && (
                        <Typography variant="body2">
                          <strong>Expiry Date:</strong> {new Date((user as any).pesticideLicenseExpiryDate).toLocaleDateString()}
                        </Typography>
                      )}
                      {(user as any).pesticideLicenseDocumentUrl && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Document:</strong>{' '}
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                              const docUrl = (user as any).pesticideLicenseDocumentUrl;
                              if (docUrl.startsWith('data:')) {
                                // For base64 data URIs, create a blob and open in new window
                                const byteString = atob(docUrl.split(',')[1]);
                                const mimeString = docUrl.split(',')[0].split(':')[1].split(';')[0];
                                const ab = new ArrayBuffer(byteString.length);
                                const ia = new Uint8Array(ab);
                                for (let i = 0; i < byteString.length; i++) {
                                  ia[i] = byteString.charCodeAt(i);
                                }
                                const blob = new Blob([ab], { type: mimeString });
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                                // Clean up the URL after a delay
                                setTimeout(() => URL.revokeObjectURL(url), 100);
                              } else {
                                window.open(docUrl, '_blank');
                              }
                            }}
                            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                          >
                            View License Document
                          </Button>
                        </Typography>
                      )}
                      {(user as any).pesticideLicenseSubmittedDate && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Submitted:</strong> {new Date((user as any).pesticideLicenseSubmittedDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Pesticide License Form */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="License Number"
                        name="pesticideLicenseNumber"
                        value={pesticideLicenseForm.pesticideLicenseNumber}
                        onChange={handlePesticideLicenseFormChange}
                        placeholder="Enter your IDOA license number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Expiry Date (Optional)"
                        name="pesticideLicenseExpiryDate"
                        type="date"
                        value={pesticideLicenseForm.pesticideLicenseExpiryDate}
                        onChange={handlePesticideLicenseFormChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <input
                          accept="image/*,application/pdf"
                          style={{ display: 'none' }}
                          id="pesticide-license-document"
                          type="file"
                          onChange={handlePesticideLicenseDocumentUpload}
                          disabled={pesticideLicenseUploading}
                        />
                        <label htmlFor="pesticide-license-document">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={pesticideLicenseUploading ? <CircularProgress size={20} /> : <CloudUpload />}
                            disabled={pesticideLicenseUploading}
                            fullWidth
                          >
                            {pesticideLicenseDocument ? 'License Document Ready ✓' : 'Upload Pesticide License Document'}
                          </Button>
                        </label>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                          Upload IDOA Commercial Pesticide Applicator License (PDF, JPG, PNG, max 10MB)
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Button
                    variant="contained"
                    onClick={handlePesticideLicenseSubmit}
                    disabled={pesticideLicenseUploading || !pesticideLicenseDocument || !pesticideLicenseForm.pesticideLicenseNumber}
                    sx={{ mt: 2 }}
                  >
                    {pesticideLicenseUploading ? <CircularProgress size={20} /> : 'Update Pesticide License Information'}
                  </Button>
                </Box>
              )}

              {/* Stripe Connect Account Setup - Only for Providers */}
              {user?.userType === 'provider' && (
                <Box sx={{ mt: 4 }}>
                  <ProviderStripeConnect />
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, zipCode: '', affectedBids: 0 })}
      >
        <DialogTitle>Remove Service Area</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove zip code "{confirmDialog.zipCode}" from your service areas?
            {confirmDialog.affectedBids > 0 && (
              <>
                <br /><br />
                <strong>Warning:</strong> This will cancel {confirmDialog.affectedBids} of your pending bid(s) in this area.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, zipCode: '', affectedBids: 0 })}>
            Cancel
          </Button>
          <Button onClick={confirmRemoveZipCode} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfilePage;

